import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';

import {
    checkAttachmentName,
    findCategoryById, getIndexBySection,
    isChildOfCategory,
} from '@features/category/application/services/category.utils';
import { MenuCategory } from '@features/category/domain/models/menu-category.model';
import { SidebarPublicModels, SidebarPublicPort } from '@features/sidebar';
import { ABTestingService } from '@shared/ab-testing/services/AB-testing.service';
import { HeaderEventAnalytics } from '@shared/analytics/store/app-events-analytics.actions';
import { AttachmentsEnums } from '@shared/header/services/attachments.enums';
import {
    CloseMenu,
    NavigateToCategoryPageAnalytics, OpenMenu,
    SetActiveCategoryIdLvl1,
} from '@shared/menu-categories/store/menu-categories.actions';
import { ActiveCategoriesTree } from '@shared/menu-categories/store/menu-categories.state';
import { GoToCategory, GoToSection } from '@shared/navigation';

@Injectable()
export class HeaderService {
    private readonly store = inject(Store);

    private readonly abTestingService = inject(ABTestingService);

    public getCategoriesLevelsById(
        id: string,
        menuCategories: MenuCategory[],
    ): Record<number, ActiveCategoriesTree> {
        const activeCategories: Record<number, ActiveCategoriesTree> = {};
        menuCategories.forEach((_, index) => {
            activeCategories[index] = this.getCategoryLevelsById(id, menuCategories, index);
        });
        return activeCategories;
    }

    public getCategoryLevelsById(
        id: string,
        menuCategories: MenuCategory[],
        indexCategory: number,
    ): ActiveCategoriesTree {
        const menuCategory = findCategoryById(menuCategories, id);

        const validIndex = indexCategory === -1 ? 0 : indexCategory;

        let firstLevelCategory = menuCategories[validIndex].subcategories.find(cat =>
            isChildOfCategory(cat, menuCategory?.id));

        if (!firstLevelCategory && menuCategory?.redirectToCategoryId) {
            firstLevelCategory = menuCategories[validIndex].subcategories.find(cat =>
                isChildOfCategory(cat, menuCategory.redirectToCategoryId));
        }

        const defaultActiveCategoryLvl1 = menuCategories[validIndex].subcategories.find(cat =>
            checkAttachmentName(cat, AttachmentsEnums.SHOW_SUBCATEGORIES));

        let secondLevelCategory: MenuCategory | undefined;
        if (firstLevelCategory && menuCategory?.id) {
            secondLevelCategory = firstLevelCategory.subcategories.find(cat =>
                isChildOfCategory(cat, menuCategory.id));
            if (!secondLevelCategory && menuCategory.redirectToCategoryId) {
                secondLevelCategory = firstLevelCategory.subcategories.find(cat =>
                    isChildOfCategory(cat, menuCategory.redirectToCategoryId));
            }
        }

        let thirdLevelCategory: MenuCategory | undefined;
        if (secondLevelCategory && menuCategory?.id) {
            thirdLevelCategory = secondLevelCategory.subcategories?.find(cat =>
                isChildOfCategory(cat, menuCategory.id));
            if (!thirdLevelCategory && menuCategory.redirectToCategoryId) {
                thirdLevelCategory = secondLevelCategory.subcategories?.find(cat =>
                    isChildOfCategory(cat, menuCategory.redirectToCategoryId));
            }
        }

        return {
            activeCategoryIdLvl1: firstLevelCategory?.id ?? defaultActiveCategoryLvl1?.id,
            activeCategoryIdLvl2: secondLevelCategory?.id,
            activeCategoryIdLvl3: thirdLevelCategory?.id,
        };
    }

    public getDefaultActiveCategory(
        section: string,
        menuCategories: MenuCategory[],
    ): MenuCategory | undefined {
        const index = getIndexBySection(section);
        const category = menuCategories[index];
        return category.subcategories.find(cat =>
            !cat.isMenuHidden
            && checkAttachmentName(cat, AttachmentsEnums.SHOW_SUBCATEGORIES));
    }

    public openShopCart(): void {
        this.store.dispatch(HeaderEventAnalytics({ section: 'cart' }));
        if (this.abTestingService.getShopCartStepVariant('goToShopCartFromHeaderButton')) {
            this.store.dispatch(GoToSection({ url: 'shopCart', reload: true }));
        } else {
            SidebarPublicPort(this.store).openSidebar(SidebarPublicModels.view.MINI_CART);
        }
    }

    public openCategory(category: MenuCategory, index: number): void {
        this.store.dispatch(NavigateToCategoryPageAnalytics({ category }));
        const categoryId = category.redirectToCategoryId ?? category.id;

        const shouldNavigateToCategory = !category.isExternalUrl
        // biome-ignore lint/complexity/useSimplifiedLogicExpression: <explanation>
            && (!category.subcategories.length || !category.isInformativeClickable
                || category.isInformativeClickable && category.redirectToCategoryId);

        if (shouldNavigateToCategory) {
            this.store.dispatch(CloseMenu());
            this.store.dispatch(GoToCategory({ categoryId }));
        }

        if (category.isExternalUrl) {
            this.store.dispatch(GoToSection({ url: category.locationPath, reload: true }));
        }

        this.store.dispatch(SetActiveCategoryIdLvl1({ activeCategoryIdLvl1: category.id, index }));
    }

    public openSubCategory(
        category: MenuCategory,
    ): void {
        this.store.dispatch(NavigateToCategoryPageAnalytics({ category }));
        const categoryId = category.redirectToCategoryId ?? category.id;

        // NO NAVEGACION
        if (category.isInformativeClickable && !categoryId) {
            return;
        }

        // NAVEGACION PAGINA INTERNA CON ID
        // biome-ignore lint/complexity/useSimplifiedLogicExpression: <explanation>
        if (!category.isExternalUrl && !category.redirectToCategoryId) {
            this.store.dispatch(CloseMenu());
            this.store.dispatch(GoToCategory({ categoryId: category.id }));
        }

        // NAVEGACION PAGINA INTERNA CON REDIRECT-ID
        if (!category.isExternalUrl && category.redirectToCategoryId) {
            this.store.dispatch(CloseMenu());
            this.store.dispatch(GoToCategory({ categoryId: category.redirectToCategoryId }));
            return;
        }

        // NAVEGACION PAGINA EXTERNA
        if (category.isExternalUrl && !category.redirectToCategoryId) {
            this.store.dispatch(CloseMenu());
            this.store.dispatch(GoToSection({ url: category.locationPath, reload: true, isExternal: true }));
            return;
        }

        // NAVEGACION POR DEFECTO
        this.store.dispatch(CloseMenu());
        this.store.dispatch(GoToCategory({ categoryId: categoryId }));
    }

    // MOVE FEATURE MENU
    public openMenuByCategoryId(
        activeCategoryIdLvl1: string,
        index: number,
    ): void {
        this.store.dispatch(OpenMenu());
        this.store.dispatch(SetActiveCategoryIdLvl1({ activeCategoryIdLvl1, index }));
    }
}
